import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Chart,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TrafficDataRow, TrafficType, AccountType, webFilterService } from '../../services/webFilter.service';
import toast from 'react-hot-toast';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Props {
  rows: TrafficDataRow[];
  trafficType: TrafficType;
  period: string;
  companyGroup: string;
  isPdfMode?: boolean;
  canEdit?: boolean;
  onNoteUpdate?: (recordDate: string, accountType: AccountType, note: string | null) => void;
}

type ChartKey = 'personal' | 'shared';

interface HoveredBar {
  chartKey: ChartKey;
  barIndex: number;
  x: number;
  y: number;
}

interface PopoverState {
  chartKey: ChartKey;
  barIndex: number;
  note: string;
  recordDate: string;
}

const TICK_DAYS = new Set([1, 7, 13, 19, 25, 30, 31]);
const NOTE_MAX_LEN = 200;
const TRAFFIC_LABEL: Record<TrafficType, string> = { web: '웹사이트', netapp: '넷앱/HTTPS' };
const ICON_MAP: Record<TrafficType, string> = { web: '🌐', netapp: '🔒' };

const WfTrafficChart: React.FC<Props> = ({
  rows,
  trafficType,
  period,
  companyGroup,
  isPdfMode,
  canEdit,
  onNoteUpdate,
}) => {
  const [localRows, setLocalRows] = useState<TrafficDataRow[]>(rows);
  const [hoveredBar, setHoveredBar] = useState<HoveredBar | null>(null);
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const [saving, setSaving] = useState(false);
  // 막대 pixel 좌표 캐시 — HTML 말풍선 위치 계산용
  const [barPositions, setBarPositions] = useState<Record<ChartKey, { x: number; y: number }[]>>({
    personal: [],
    shared: [],
  });

  const personalChartRef = useRef<Chart<'bar'>>(null);
  const sharedChartRef = useRef<Chart<'bar'>>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const personalWrapperRef = useRef<HTMLDivElement>(null);
  const sharedWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setLocalRows(rows); }, [rows]);

  // 차트 렌더 후 막대 pixel 좌표 추출 → HTML 말풍선 위치에 사용
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const extract = (ref: React.RefObject<Chart<'bar'>>) => {
        const chart = ref.current;
        if (!chart) return [] as { x: number; y: number }[];
        return chart.getDatasetMeta(0).data.map((el: any) => ({ x: el.x as number, y: el.y as number }));
      };
      setBarPositions({
        personal: extract(personalChartRef),
        shared: extract(sharedChartRef),
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [localRows]);

  // 팝오버 바깥 클릭 시 닫기
  useEffect(() => {
    if (!popover) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopover(null);
        setHoveredBar(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popover]);

  const getDataForKey = useCallback(
    (chartKey: ChartKey) =>
      localRows.filter((r) => r.accountType === chartKey),
    [localRows],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, chartKey: ChartKey, chartRef: React.RefObject<Chart<'bar'>>) => {
      if (!canEdit || !chartRef.current || popover) return;
      const chart = chartRef.current;
      const elements = chart.getElementsAtEventForMode(e.nativeEvent, 'nearest', { intersect: true }, false);
      if (elements.length === 0) {
        // 막대 밖이어도 wrapper 안에 있으면 유지 (배지 클릭 가능하도록)
        // 실제 초기화는 onMouseLeave(wrapper 완전 이탈)에서만
        return;
      }
      const { datasetIndex, index } = elements[0];
      const el = chart.getDatasetMeta(datasetIndex).data[index] as any;
      const canvasRect = chart.canvas.getBoundingClientRect();
      const containerRect = e.currentTarget.getBoundingClientRect();
      const relX = canvasRect.left - containerRect.left + el.x;
      const relY = canvasRect.top - containerRect.top + el.y;
      setHoveredBar({ chartKey, barIndex: index, x: relX, y: relY });
    },
    [canEdit, popover],
  );

  const handleMouseLeave = useCallback(() => {
    if (!popover) setHoveredBar(null);
  }, [popover]);

  const openPopover = useCallback(
    (chartKey: ChartKey, barIndex: number, x: number, y: number) => {
      const data = getDataForKey(chartKey);
      const row = data[barIndex];
      if (!row) return;
      setPopover({ chartKey, barIndex, note: row.note ?? '', recordDate: row.recordDate });
    },
    [getDataForKey],
  );

  const handleSave = useCallback(async () => {
    if (!popover) return;
    setSaving(true);
    try {
      const noteToSave = popover.note.trim() || null;
      await webFilterService.updateTrafficNote({
        period,
        companyGroup,
        trafficType,
        accountType: popover.chartKey as AccountType,
        recordDate: popover.recordDate,
        note: noteToSave,
      });
      setLocalRows((prev) =>
        prev.map((r) =>
          r.recordDate === popover.recordDate && r.accountType === popover.chartKey
            ? { ...r, note: noteToSave }
            : r,
        ),
      );
      onNoteUpdate?.(popover.recordDate, popover.chartKey as AccountType, noteToSave);
      toast.success('메모가 저장되었습니다.');
      setPopover(null);
      setHoveredBar(null);
    } catch {
      toast.error('메모 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [popover, period, companyGroup, trafficType]);

  const handleDelete = useCallback(async () => {
    if (!popover) return;
    setSaving(true);
    try {
      await webFilterService.updateTrafficNote({
        period,
        companyGroup,
        trafficType,
        accountType: popover.chartKey as AccountType,
        recordDate: popover.recordDate,
        note: null,
      });
      setLocalRows((prev) =>
        prev.map((r) =>
          r.recordDate === popover.recordDate && r.accountType === popover.chartKey
            ? { ...r, note: null }
            : r,
        ),
      );
      onNoteUpdate?.(popover.recordDate, popover.chartKey as AccountType, null);
      toast.success('메모가 삭제되었습니다.');
      setPopover(null);
      setHoveredBar(null);
    } catch {
      toast.error('메모 삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [popover, period, companyGroup, trafficType]);

  const buildChart = (chartKey: ChartKey, title: string, color: string) => {
    const data = getDataForKey(chartKey);
    const chartRef = chartKey === 'personal' ? personalChartRef : sharedChartRef;
    const labels = data.map((r) => r.recordDate.slice(5));
    const values = data.map((r) => r.blockedCount);
    const positions = barPositions[chartKey];

    const isHovered = hoveredBar?.chartKey === chartKey;
    const hoveredRow = isHovered ? data[hoveredBar!.barIndex] : null;
    const hasNote = !!hoveredRow?.note;

    const wrapperRef = chartKey === 'personal' ? personalWrapperRef : sharedWrapperRef;
    const POPOVER_WIDTH = 260;
    const TITLE_HEIGHT = 30; // .wf-chart-title 높이 보정

    const popoverEl = canEdit && !isPdfMode && popover?.chartKey === chartKey ? (() => {
      const containerWidth = wrapperRef.current?.offsetWidth ?? 600;
      const barX = hoveredBar?.x ?? 0;
      const rawLeft = barX - POPOVER_WIDTH / 2;
      const clampedLeft = Math.max(0, Math.min(rawLeft, containerWidth - POPOVER_WIDTH));
      // top: wrapper 내 bar.y + title 높이 보정, 팝오버 높이만큼 위로
      const popoverTop = TITLE_HEIGHT + (hoveredBar?.y ?? 120) - 185;
      return (
        <div
          ref={popoverRef}
          style={{
            position: 'absolute',
            left: clampedLeft,
            top: Math.max(0, popoverTop),
            width: POPOVER_WIDTH,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            padding: 14,
            zIndex: 100,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: '#475569' }}>
            📅 {popover.recordDate} 메모
          </div>
          <textarea
            value={popover.note}
            onChange={(e) =>
              setPopover((p) => p && { ...p, note: e.target.value.slice(0, NOTE_MAX_LEN) })
            }
            rows={4}
            maxLength={NOTE_MAX_LEN}
            placeholder="메모를 입력하세요..."
            style={{
              width: '100%',
              resize: 'vertical',
              fontSize: 12,
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              padding: '6px 8px',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
          <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right', marginBottom: 8 }}>
            {popover.note.length}/{NOTE_MAX_LEN}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                flex: 1, padding: '6px 0', background: '#3b82f6', color: '#fff',
                border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
              }}
            >
              저장
            </button>
            {localRows.find(
              (r) => r.recordDate === popover.recordDate && r.accountType === popover.chartKey,
            )?.note && (
              <button
                onClick={handleDelete}
                disabled={saving}
                style={{
                  padding: '6px 10px', background: '#ef4444', color: '#fff',
                  border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                }}
              >
                🗑
              </button>
            )}
            <button
              onClick={() => { setPopover(null); setHoveredBar(null); }}
              style={{
                padding: '6px 10px', background: '#e2e8f0', color: '#475569',
                border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              취소
            </button>
          </div>
        </div>
      );
    })() : null;

    return (
      // position: relative — 팝오버가 wrapper 밖(카드 레벨)에서 절대 위치 잡도록
      <div className="wf-chart-card" style={{ position: 'relative' }}>
        <div className="wf-chart-title">■ {title}</div>
        <div
          ref={wrapperRef}
          className="wf-chart-wrapper"
          style={{ height: 280, position: 'relative' }}
          onMouseMove={(e) => handleMouseMove(e, chartKey, chartRef)}
          onMouseLeave={handleMouseLeave}
        >
          <Bar
            ref={chartRef}
            plugins={[ChartDataLabels]}
            data={{
              labels,
              datasets: [{ label: '차단 횟수', data: values, backgroundColor: color }],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    afterBody: (items) => {
                      const note = data[items[0]?.dataIndex]?.note;
                      return note ? `💬 ${note}` : '';
                    },
                  },
                },
                datalabels: {
                  anchor: 'end',
                  align: 'top',
                  font: { size: 10 },
                  formatter: (v: number) => v.toLocaleString('ko-KR'),
                  color: '#333',
                },
              },
              scales: {
                x: {
                  grid: { display: false },
                  ticks: {
                    callback: (_val: any, index: number) => {
                      const dateStr = labels[index];
                      if (!dateStr) return null;
                      const day = parseInt(dateStr.split('-')[1] ?? dateStr, 10);
                      return TICK_DAYS.has(day) ? dateStr : null;
                    },
                    maxRotation: 0,
                    autoSkip: false,
                  },
                },
                y: {
                  beginAtZero: true,
                  grace: '15%',
                  grid: { color: 'rgba(0,0,0,0.08)' },
                },
              },
            }}
          />

          {/* 호버 배지 */}
          {canEdit && !isPdfMode && isHovered && hoveredRow && !popover && (
            <button
              style={{
                position: 'absolute',
                left: hoveredBar!.x,
                top: hoveredBar!.y - 32,
                transform: 'translateX(-50%)',
                background: hasNote ? '#F59E0B' : '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '3px 8px',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                zIndex: 10,
              }}
              onClick={() => openPopover(chartKey, hoveredBar!.barIndex, hoveredBar!.x, hoveredBar!.y)}
            >
              {hasNote ? '💬 메모 수정' : '＋ 메모 추가'}
            </button>
          )}
        </div>

        {/* HTML 말풍선 — wrapper 밖 card 레벨, overflow clipping 없음 */}
        {!isPdfMode && data.map((r, i) => {
          if (!r.note || !positions[i]) return null;
          const pos = positions[i];
          const containerWidth = wrapperRef.current?.offsetWidth ?? 600;
          const BUBBLE_WIDTH = 180;
          const rawLeft = pos.x - BUBBLE_WIDTH / 2;
          const left = Math.max(0, Math.min(rawLeft, containerWidth - BUBBLE_WIDTH));
          // wrapper 상단에서 8px 고정 (TITLE_HEIGHT 보정)
          const top = TITLE_HEIGHT + 8;
          // 꼬리: wrapper 내 막대 상단까지 선으로 연결
          const tailTop = TITLE_HEIGHT + pos.y;
          const tailLeft = pos.x;

          return (
            <React.Fragment key={`bubble-${chartKey}-${i}`}>
              {/* 말풍선 박스 */}
              <div
                style={{
                  position: 'absolute',
                  left,
                  top,
                  width: BUBBLE_WIDTH,
                  background: '#fffbeb',
                  border: '1.5px solid #F59E0B',
                  borderRadius: 8,
                  padding: '6px 10px',
                  fontSize: 12,
                  color: '#1e293b',
                  lineHeight: 1.5,
                  wordBreak: 'break-all',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.12)',
                  pointerEvents: 'none',
                  zIndex: 5,
                }}
              >
                {r.note}
                {/* 꼬리 삼각형 (하단 중앙) */}
                <div style={{
                  position: 'absolute',
                  left: pos.x - left - 6,
                  bottom: -9,
                  width: 0, height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: '8px solid #F59E0B',
                }} />
                <div style={{
                  position: 'absolute',
                  left: pos.x - left - 5,
                  bottom: -7,
                  width: 0, height: 0,
                  borderLeft: '5px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: '7px solid #fffbeb',
                }} />
              </div>
              {/* 꼬리 연장선: 삼각형 끝 → 막대 상단 */}
              <div style={{
                position: 'absolute',
                left: tailLeft - 0.5,
                top: top + 40,  // 말풍선 하단 근사
                width: 1,
                height: Math.max(0, tailTop - (top + 40)),
                background: '#F59E0B',
                pointerEvents: 'none',
                zIndex: 4,
              }} />
            </React.Fragment>
          );
        })}

        {/* 팝오버 */}
        {popoverEl}
      </div>
    );
  };

  return (
    <div className={`wf-section-card ${isPdfMode ? 'pdf-page-break' : ''}`}>
      {isPdfMode && (
        <h2 className="wf-pdf-section-title">
          {ICON_MAP[trafficType]} {trafficType === 'web' ? '웹/넷앱 트래픽 (1/2)' : '웹/넷앱 트래픽 (2/2)'}
        </h2>
      )}
      {buildChart('personal', `임직원 PC 일별 ${TRAFFIC_LABEL[trafficType]} 차단 횟수`, 'rgba(52, 152, 219, 0.8)')}
      {buildChart('shared', `공용 PC 일별 ${TRAFFIC_LABEL[trafficType]} 차단 횟수`, 'rgba(52, 152, 219, 0.8)')}
    </div>
  );
};

export default WfTrafficChart;
